import { PALETTE } from "@eldrun/content";
import { BufferAttribute, BufferGeometry, DynamicDrawUsage, Mesh, type Object3D, type ShaderMaterial, type Vector3 } from "three";
import { QUALITY } from "../render/quality.ts";
import { createSkillMeshMaterial } from "../render/shaders/skill-mesh.ts";

/** Amostras guardadas por fita (o anel da cauda; Fase 14: 32, para a meia-lua longa seguir curva); a ponta viva entra à parte. */
export const RIBBON_SAMPLES = 32;
/** Pontos de uma fita no máximo: a ponta viva, as amostras e o corte da cauda. */
export const RIBBON_POINTS = RIBBON_SAMPLES + 2;
/** Vértices de uma faixa da fita no máximo: dois triângulos sem índice por trecho. */
export const RIBBON_VERTICES = (RIBBON_POINTS - 1) * 6;

/**
 * Uma faixa da fita (Gate B5; a referência do golpe): o trecho de `a` (0) a `b` (1) que ela cobre, o
 * branco somado à cor da classe (o núcleo quente), a opacidade e os extras do brilho e do preenchimento.
 * Todas as faixas são a mesma cauda no tempo, na mesma malha e no mesmo draw call.
 */
export interface RibbonStrip {
  from: number;
  to: number;
  white: number;
  alpha: number;
  glow: number;
  fill: number;
}

/** Faixas por fita no máximo: o corpo, o núcleo e dois fios. */
export const RIBBON_STRIPS = 4;
/** O corpo: a fita inteira, só na cor da classe (o que o Baixo desenha). */
export const RIBBON_BODY: RibbonStrip = { from: 0, to: 1, white: 0, alpha: 1, glow: 0, fill: 0 };
/**
 * As faixas de cada fita, da mais importante à mais decorativa: o corpo translúcido inteiro, o núcleo
 * claro (na ponta da lâmina; no meio da flecha) e, no golpe, dois fios finos atrás da ponta.
 */
const STRIPS: Readonly<Record<RibbonKind, readonly RibbonStrip[]>> = {
  golpe: [
    RIBBON_BODY,
    { from: 0.82, to: 1, white: 0.5, alpha: 1, glow: 0.8, fill: 0.45 },
    { from: 0.56, to: 0.6, white: 0.15, alpha: 0.6, glow: 0.3, fill: 0.3 },
    { from: 0.68, to: 0.71, white: 0.15, alpha: 0.5, glow: 0.3, fill: 0.3 },
  ],
  flecha: [RIBBON_BODY, { from: 0.4, to: 0.6, white: 0.5, alpha: 1, glow: 0.8, fill: 0.45 }],
  investida: [RIBBON_BODY, { from: 0.4, to: 0.6, white: 0.35, alpha: 1, glow: 0.6, fill: 0.4 }],
};
/** Quantas faixas por tier: o Baixo só o corpo, o Médio com o núcleo, o Alto com os fios. */
const TIER_STRIPS = { baixo: 1, medio: 2, alto: 4 } as const;

/** Como a fita sai: cor, força, brilho e o tamanho da cauda. */
export interface RibbonLook {
  /**
   * Folha (Fase 14, a meia-lua da referência): o preenchimento e as bordas crescem do cabo (`a`) para a
   * ponta (`b`) e apagam com a idade; sem ela, a face e as duas bordas são iguais (flecha, Investida).
   */
  sheet?: boolean;
  color: string;
  /** Opacidade (os "Efeitos dos aliados", F68). */
  alpha: number;
  /** Brilho das arestas (acima de 1 o bloom pega); "Reduzir lampejos" baixa. */
  glow: number;
  /** Preenchimento da face (0 a 1). */
  fill: number;
  /** Idade (ms) em que a largura chega a zero: o comprimento da cauda no tempo. */
  lifeMs: number;
  /** Para onde a largura afina, de `a` (0) a `b` (1): a ponta da arma (1) ou o meio (0,5). */
  pivot: number;
  /** As faixas desenhadas (o corpo primeiro). */
  strips: readonly RibbonStrip[];
}

/**
 * Fitas dos golpes ao mesmo tempo: os 4 jogadores golpeando em sequência, cada um com a fita do golpe e a
 * cauda do anterior ainda sumindo.
 */
export const SWING_RIBBONS = 8;

/** Do item na mão, a fita vai desta fração da altura até a ponta (`swingTip`). */
export const SWING_BASE = 0.25;
/** A energia passa da ponta da arma (m, Fase 14): a meia-lua da referência é maior que a lâmina. */
export const SWING_REACH = 0.45;
/** Limites da ponta da fita (m, no tamanho do conteúdo). */
const SWING_TIP = { min: 0.3, max: 1.6 } as const;
/**
 * Corte de teleporte/reconciliação: folga de 4 m para a rotação da arma, mais 80 m/s de movimento
 * por até 250 ms (inclui flechas rápidas e quadros lentos). Uma pausa maior não autoriza ligar dois
 * pontos arbitrariamente distantes. A medida usa os dois lados da última ponta viva, não a cauda.
 */
const CONTINUITY = { slack: 4, speed: 80, maxSeconds: 0.25 } as const;

/**
 * Onde a fita do golpe acaba no item (no espaço da malha, ao longo do cabo, +y): a ponta da caixa da
 * geometria, presa entre os limites. Os modelos e as malhas de reserva estão no tamanho do conteúdo (m), e
 * a escala da pose só desfaz a do encaixe da mão (`1 / AVATAR_SCALE` nos KayKit; 1,1 na reserva), então os
 * limites valem em metros no mundo. A fita começa em `SWING_BASE` dela.
 */
export function swingTip(geometry: BufferGeometry): number {
  const box = geometry.boundingBox ?? (geometry.computeBoundingBox(), geometry.boundingBox!);
  return Math.min(SWING_TIP.max, Math.max(SWING_TIP.min, box.max.y));
}

/**
 * Os dois lados da fita do golpe no mundo, na pose atual do item: de `SWING_BASE` da altura até a ponta
 * mais o alcance da energia (`SWING_REACH`). Quem chama atualiza a matriz do item antes.
 */
export function swingEnds(held: Object3D & { geometry: BufferGeometry }, base: Vector3, tip: Vector3): void {
  const top = swingTip(held.geometry);
  held.localToWorld(base.set(0, top * SWING_BASE, 0));
  held.localToWorld(tip.set(0, top + SWING_REACH, 0));
}

/** As fitas do jogo: a do golpe (entre a base e a ponta do item, afinando para a ponta), a da flecha e a da Investida (o corpo em pé). */
export type RibbonKind = "golpe" | "flecha" | "investida";

/** Brilho das arestas de cada fita, normal e com "Reduzir lampejos" (abaixo de 1: sem bloom). */
const RIBBON_STYLE: Readonly<Record<RibbonKind, Omit<RibbonLook, "color" | "alpha" | "glow" | "strips"> & { glow: number; reduced: number }>> = {
  // Golpe (Fase 14): a folha em meia-lua; a cauda mais longa no Médio e no Alto (`LONG_SWING_MS`).
  golpe: { lifeMs: 280, fill: 0.32, pivot: 1, glow: 2.2, reduced: 1, sheet: true },
  flecha: { lifeMs: 140, fill: 0.4, pivot: 0.5, glow: 2, reduced: 1 },
  // Investida (Gate C): uma faixa em pé do joelho ao peito pelo caminho real do corpo, afinando ao meio.
  investida: { lifeMs: 240, fill: 0.3, pivot: 0.5, glow: 2.2, reduced: 1 },
};

/** A cauda do golpe no Médio e no Alto (ms, Fase 14): a meia-lua longa da referência; o Baixo fica nos 280. */
export const LONG_SWING_MS = 420;

/**
 * O visual de uma fita na cor `color`, com a força `alpha` ("Efeitos dos aliados") e o brilho pela opção;
 * as faixas pelo tier (`QUALITY.post.tier`). "Reduzir lampejos" tira o branco e o brilho extra do núcleo.
 */
export function ribbonLook(kind: RibbonKind, color: string, alpha: number, reduceFlashes: boolean, tier: keyof typeof TIER_STRIPS = QUALITY.post.tier): RibbonLook {
  const s = RIBBON_STYLE[kind];
  const strips = STRIPS[kind].slice(0, TIER_STRIPS[tier]).map((strip) => (reduceFlashes ? { ...strip, white: strip.white * 0.4, glow: 0 } : strip));
  const lifeMs = kind === "golpe" && tier !== "baixo" ? LONG_SWING_MS : s.lifeMs;
  return { color, alpha, glow: reduceFlashes ? s.reduced : s.glow, fill: s.fill, lifeMs, pivot: s.pivot, strips, ...(s.sheet && { sheet: true }) };
}

/**
 * O rastro de uma fita (F92), puro para os testes: um anel pré-alocado de amostras de dois pontos (`a` e
 * `b`, a base e a ponta da arma ou os lados da flecha) e a ponta viva, que segue o objeto a cada quadro.
 * As amostras saem pelo tempo, de `step` em `step` ms, interpoladas entre a última e a ponta viva (no
 * arco, girando a direção de `a` para `b`): com quadros lentos a fita continua inteira e curva, sem os
 * buracos do rastro de estilhaços.
 */
export class RibbonTrail {
  /** O instante de cada amostra (ms), no anel. */
  private readonly times = new Float64Array(RIBBON_SAMPLES);
  /** Os dois pontos de cada amostra: `a` (x, y, z) e `b` (x, y, z). */
  private readonly points = new Float32Array(RIBBON_SAMPLES * 6);
  private readonly tip = new Float32Array(6);
  /** Os pontos da fita deste quadro, já afinados (da ponta à cauda). */
  private readonly shaped = new Float32Array(RIBBON_POINTS * 6);
  /** A idade de cada ponto afinado, de 0 (a ponta viva) a 1 (o corte da cauda). */
  private readonly shapedAge = new Float32Array(RIBBON_POINTS);
  private head = -1;
  private count = 0;
  private tipAt = 0;
  private live = false;
  private releasedAt = 0;
  private lifeMs = 160;
  private step = 160 / (RIBBON_SAMPLES - 1);

  /** Começa de novo, vazia; a cauda dura `lifeMs`. As amostras cobrem a vida inteira. */
  reset(lifeMs: number): void {
    this.lifeMs = lifeMs;
    this.step = lifeMs / (RIBBON_SAMPLES - 1);
    this.head = -1;
    this.count = 0;
    this.live = true;
  }

  get samples(): number {
    return this.count;
  }

  get active(): boolean {
    return this.live;
  }

  /** A ponta viva em `now`. Guarda as amostras vencidas desde a última, no arco até a ponta. */
  sample(now: number, ax: number, ay: number, az: number, bx: number, by: number, bz: number): void {
    if (!this.live) return;
    const tip = this.tip;
    if (this.count > 0) {
      const dt = now - this.tipAt;
      const reach = CONTINUITY.slack + CONTINUITY.speed * Math.min(CONTINUITY.maxSeconds, Math.max(0, dt) / 1000);
      const a2 = (ax - tip[0]!) ** 2 + (ay - tip[1]!) ** 2 + (az - tip[2]!) ** 2;
      const b2 = (bx - tip[3]!) ** 2 + (by - tip[4]!) ** 2 + (bz - tip[5]!) ** 2;
      if (dt < 0 || Math.max(a2, b2) > reach * reach) this.reset(this.lifeMs);
    }
    tip[0] = ax;
    tip[1] = ay;
    tip[2] = az;
    tip[3] = bx;
    tip[4] = by;
    tip[5] = bz;
    this.tipAt = now;
    if (this.count === 0) {
      this.commit(now, tip);
      return;
    }
    const last = this.times[this.head]!;
    const gap = now - last;
    if (gap < this.step) return;
    // Uma parada longa (aba escondida): só as últimas amostras importam.
    const skip = Math.max(0, Math.floor(gap / this.step) - RIBBON_SAMPLES);
    let t = last + this.step * (skip + 1);
    const from = this.head * 6;
    // Da última amostra (guardada antes de o anel andar) até a ponta viva: `a` anda em reta e a direção
    // de `a` para `b` gira (interpolação esférica), com o comprimento entre os dois. Num quadro longo a
    // ponta segue o arco do golpe, não a corda (Gate B5; o parecer externo).
    const p = this.points;
    const ax0 = p[from]!, ay0 = p[from + 1]!, az0 = p[from + 2]!;
    let ux0 = p[from + 3]! - ax0, uy0 = p[from + 4]! - ay0, uz0 = p[from + 5]! - az0;
    let ux1 = tip[3] - tip[0], uy1 = tip[4] - tip[1], uz1 = tip[5] - tip[2];
    const len0 = Math.hypot(ux0, uy0, uz0);
    const len1 = Math.hypot(ux1, uy1, uz1);
    if (len0 > 1e-6) {
      ux0 /= len0;
      uy0 /= len0;
      uz0 /= len0;
    }
    if (len1 > 1e-6) {
      ux1 /= len1;
      uy1 /= len1;
      uz1 /= len1;
    }
    // Só um lado com direção (o outro de comprimento zero): vale a direção que existe, e o comprimento
    // é interpolado uma vez só (revisão do Gate B).
    if (len0 <= 1e-6) [ux0, uy0, uz0] = [ux1, uy1, uz1];
    if (len1 <= 1e-6) [ux1, uy1, uz1] = [ux0, uy0, uz0];
    // Giro no plano das duas direções: u(k) = cos(kθ)·u0 + sin(kθ)·v, com v perpendicular a u0.
    const dot = Math.min(1, Math.max(-1, ux0 * ux1 + uy0 * uy1 + uz0 * uz1));
    let angle = Math.acos(dot);
    let vx = ux1 - ux0 * dot;
    let vy = uy1 - uy0 * dot;
    let vz = uz1 - uz0 * dot;
    let vl = Math.hypot(vx, vy, vz);
    if (vl < 1e-6 && dot < 0) {
      // Meia-volta: o plano não está definido; um eixo fixo do mundo (o menos paralelo à lâmina) faz
      // dela um arco, e a fita não passa pelo comprimento zero da mistura linear.
      const [px, py, pz] = Math.abs(uy0) < 0.9 ? [0, 1, 0] : [1, 0, 0];
      const along = px * ux0 + py * uy0 + pz * uz0;
      vx = px - ux0 * along;
      vy = py - uy0 * along;
      vz = pz - uz0 * along;
      vl = Math.hypot(vx, vy, vz);
      angle = Math.PI;
    }
    if (vl < 1e-6) angle = 0;
    else {
      vx /= vl;
      vy /= vl;
      vz /= vl;
    }
    while (t <= now) {
      const k = (t - last) / gap;
      this.head = (this.head + 1) % RIBBON_SAMPLES;
      const o = this.head * 6;
      const c = Math.cos(k * angle);
      const sn = Math.sin(k * angle);
      const len = len0 + (len1 - len0) * k;
      const ax = ax0 + (tip[0] - ax0) * k;
      const ay = ay0 + (tip[1] - ay0) * k;
      const az = az0 + (tip[2] - az0) * k;
      p[o] = ax;
      p[o + 1] = ay;
      p[o + 2] = az;
      p[o + 3] = ax + (ux0 * c + vx * sn) * len;
      p[o + 4] = ay + (uy0 * c + vy * sn) * len;
      p[o + 5] = az + (uz0 * c + vz * sn) * len;
      this.times[this.head] = t;
      this.count = Math.min(RIBBON_SAMPLES, this.count + 1);
      t += this.step;
    }
  }

  /** Para de seguir (o golpe acabou, a flecha caiu): a ponta viva vira a última amostra e a fita some. */
  release(now: number): void {
    if (!this.live) return;
    this.live = false;
    this.releasedAt = now;
    if (this.count > 0 && this.tipAt > this.times[this.head]!) this.commit(this.tipAt, this.tip);
  }

  /** O alpha da fita inteira: 1 enquanto segue; solta, cai até 0 no tempo da cauda. */
  fade(now: number): number {
    return this.live ? 1 : Math.max(0, 1 - (now - this.releasedAt) / this.lifeMs);
  }

  /** Solta e sem nada mais novo que a cauda: acabou. */
  done(now: number): boolean {
    return !this.live && (this.count === 0 || now - this.times[this.head]! >= this.lifeMs);
  }

  /**
   * Escreve os triângulos da fita em `out` (x, y, z por vértice) e devolve quantos vértices. Com `meta`,
   * escreve também, por vértice, o lado (`from` em `a`, `to` em `b`) e a idade (0 a 1 da vida). A largura cai
   * de 1 na ponta a 0 na idade `lifeMs`, em direção ao `pivot`; o último trecho é cortado nessa idade,
   * então a cauda encolhe sem saltos. Dois triângulos por trecho: (aᵢ, bᵢ, bⱼ) e (aᵢ, bⱼ, aⱼ).
   */
  build(now: number, pivot: number, out: Float32Array, from = 0, to = 1, meta?: Float32Array): number {
    const s = this.shaped;
    const ages = this.shapedAge;
    let n = 0;
    let prevAge = 0;
    if (this.live && this.count > 0) {
      prevAge = Math.max(0, now - this.tipAt);
      ages[n] = Math.min(1, prevAge / this.lifeMs);
      this.shape(this.tip, 0, prevAge, pivot, n++);
    }
    for (let i = 0; i < this.count; i++) {
      const slot = (this.head - i + RIBBON_SAMPLES) % RIBBON_SAMPLES;
      const age = Math.max(0, now - this.times[slot]!);
      if (age < this.lifeMs) {
        ages[n] = age / this.lifeMs;
        this.shape(this.points, slot * 6, age, pivot, n++);
        prevAge = age;
        continue;
      }
      // Corte da cauda: largura 0, no pivô, entre o ponto anterior (mais novo) e este, na idade exata da
      // vida. O pivô do ponto afinado é o mesmo do ponto cru (a largura encolhe em volta dele).
      if (n > 0) {
        const k = (this.lifeMs - prevAge) / (age - prevAge);
        const d = n * 6;
        const p = d - 6;
        const o = slot * 6;
        for (let c = 0; c < 3; c++) {
          const m = this.points[o + c]! + (this.points[o + 3 + c]! - this.points[o + c]!) * pivot;
          const pm = s[p + c]! + (s[p + 3 + c]! - s[p + c]!) * pivot;
          s[d + c] = s[d + 3 + c] = pm + (m - pm) * k;
        }
        ages[n] = 1;
        n++;
      }
      break;
    }
    // A faixa: o trecho [from, to] de cada par (a, b) já afinado.
    if (from !== 0 || to !== 1) {
      for (let i = 0; i < n; i++) {
        const d = i * 6;
        for (let c = 0; c < 3; c++) {
          const a = s[d + c]!;
          const b = s[d + 3 + c]!;
          s[d + c] = a + (b - a) * from;
          s[d + 3 + c] = a + (b - a) * to;
        }
      }
    }
    let v = 0;
    for (let i = 0; i + 1 < n; i++) {
      const p = i * 6;
      const q = p + 6;
      if (meta) {
        // Os mesmos cantos dos dois triângulos abaixo: (aᵢ, bᵢ, bⱼ) e (aᵢ, bⱼ, aⱼ).
        const m = v * 2;
        const ai = ages[i]!;
        const aj = ages[i + 1]!;
        meta[m] = from;
        meta[m + 1] = ai;
        meta[m + 2] = to;
        meta[m + 3] = ai;
        meta[m + 4] = to;
        meta[m + 5] = aj;
        meta[m + 6] = from;
        meta[m + 7] = ai;
        meta[m + 8] = to;
        meta[m + 9] = aj;
        meta[m + 10] = from;
        meta[m + 11] = aj;
      }
      v = vertex(out, v, s, p);
      v = vertex(out, v, s, p + 3);
      v = vertex(out, v, s, q + 3);
      v = vertex(out, v, s, p);
      v = vertex(out, v, s, q + 3);
      v = vertex(out, v, s, q);
    }
    return v;
  }

  /** O ponto `n` da fita: os dois lados de `src[o…]`, encolhidos em volta do pivô pela idade. */
  private shape(src: Float32Array, o: number, age: number, pivot: number, n: number): void {
    const w = Math.max(0, 1 - age / this.lifeMs);
    const d = n * 6;
    const s = this.shaped;
    for (let c = 0; c < 3; c++) {
      const a = src[o + c]!;
      const b = src[o + 3 + c]!;
      const m = a + (b - a) * pivot;
      s[d + c] = m + (a - m) * w;
      s[d + 3 + c] = m + (b - m) * w;
    }
  }

  private commit(t: number, src: Float32Array): void {
    this.head = (this.head + 1) % RIBBON_SAMPLES;
    const d = this.head * 6;
    for (let c = 0; c < 6; c++) this.points[d + c] = src[c]!;
    this.times[this.head] = t;
    this.count = Math.min(RIBBON_SAMPLES, this.count + 1);
  }
}

function vertex(out: Float32Array, v: number, src: Float32Array, o: number): number {
  out[v * 3] = src[o]!;
  out[v * 3 + 1] = src[o + 1]!;
  out[v * 3 + 2] = src[o + 2]!;
  return v + 1;
}

/**
 * Baricêntricas fixas da fita: só as duas bordas compridas acendem. No triângulo (aᵢ, bᵢ, bⱼ) a aresta
 * acesa é bᵢbⱼ (oposta ao canto 0); no (aᵢ, bⱼ, aⱼ), aⱼaᵢ (oposta ao canto 1). As componentes constantes
 * em 1 escondem as outras arestas (os degraus e as diagonais), como o `withBary` faz com os quads.
 */
export function ribbonBary(): Float32Array {
  const bary = new Float32Array(RIBBON_VERTICES * 3);
  for (let i = 0; i < RIBBON_VERTICES; i++) {
    const corner = i % 3;
    const second = Math.floor(i / 3) % 2 === 1;
    const lit = second ? 1 : 0;
    for (let c = 0; c < 3; c++) bary[i * 3 + c] = c === lit ? (corner === lit ? 1 : 0) : 1;
  }
  return bary;
}

type RibbonState = "livre" | "ativa" | "soltando";

/**
 * Uma fita (F92): uma malha e um material do programa `peca-de-habilidade` (F78, já na pré-compilação),
 * numa geometria sem índice com `position` dinâmico e `aBary` fixo, sem `normal` (a mesma assinatura das
 * peças). Fora do uso, a malha fica invisível: nenhuma draw call parada.
 */
export class Ribbon {
  readonly mesh: Mesh;
  readonly trail = new RibbonTrail();
  private readonly geometry = new BufferGeometry();
  private readonly position: BufferAttribute;
  /** Por vértice, constantes por faixa (escritos no `begin`): o branco somado e a opacidade, o brilho e o preenchimento extras. */
  private readonly tint: BufferAttribute;
  private readonly params: BufferAttribute;
  private readonly material: ShaderMaterial;
  private alpha = 1;
  private pivot = 1;
  private sheet = false;
  /** O brilho e o preenchimento do `begin` (o reforço do crítico parte deles, sem somar). */
  private baseGlow = 0;
  private baseFill = 0;
  /** Lado e idade por vértice da faixa em construção (`RibbonTrail.build`). */
  private readonly meta = new Float32Array(RIBBON_VERTICES * 2);
  private strips: readonly RibbonStrip[] = [RIBBON_BODY];
  state: RibbonState = "livre";
  /** Quando foi solta (para o pool tirar a mais velha). */
  releasedAt = 0;

  constructor() {
    const capacity = RIBBON_VERTICES * RIBBON_STRIPS;
    this.position = new BufferAttribute(new Float32Array(capacity * 3), 3).setUsage(DynamicDrawUsage);
    this.geometry.setAttribute("position", this.position);
    const bary = ribbonBary();
    const barys = new Float32Array(capacity * 3);
    for (let k = 0; k < RIBBON_STRIPS; k++) barys.set(bary, k * bary.length);
    this.geometry.setAttribute("aBary", new BufferAttribute(barys, 3));
    // Os atributos "por instância" do programa das peças, aqui por vértice (cada faixa o seu).
    this.tint = new BufferAttribute(new Float32Array(capacity * 4), 4);
    this.params = new BufferAttribute(new Float32Array(capacity * 4), 4);
    this.geometry.setAttribute("iColor", this.tint);
    this.geometry.setAttribute("iParams", this.params);
    // Transformação na identidade, como atributo de verdade (não o genérico do WebGL; revisão do Gate B).
    const identity = (size: number, value: readonly number[]) => {
      const out = new Float32Array(capacity * size);
      for (let i = 0; i < capacity; i++) out.set(value, i * size);
      return new BufferAttribute(out, size);
    };
    this.geometry.setAttribute("iOffset", identity(3, [0, 0, 0]));
    this.geometry.setAttribute("iRot", identity(4, [0, 0, 0, 1]));
    this.geometry.setAttribute("iScale", identity(4, [0, 0, 0, 1]));
    this.geometry.setDrawRange(0, 0);
    // A cor de verdade entra no `begin`.
    this.material = createSkillMeshMaterial(PALETTE.neutro.hex);
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.name = "fita";
    this.mesh.visible = false;
    // A fita muda de lugar todo quadro: sem a esfera de contorno, que ficaria velha.
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 6;
  }

  /** Começa a seguir, com o visual `look` (a cor é lida aqui, não por quadro). */
  begin(look: RibbonLook): void {
    const u = this.material.uniforms;
    (u["uColor"]!.value as { set(hex: string): void }).set(look.color);
    u["uGlow"]!.value = this.baseGlow = look.glow;
    u["uFill"]!.value = this.baseFill = look.fill;
    this.alpha = look.alpha;
    this.pivot = look.pivot;
    this.sheet = look.sheet === true;
    this.strips = look.strips.slice(0, RIBBON_STRIPS);
    const tint = this.tint.array as Float32Array;
    const params = this.params.array as Float32Array;
    for (let k = 0; k < RIBBON_STRIPS; k++) {
      const strip = this.strips[k];
      for (let i = k * RIBBON_VERTICES; i < (k + 1) * RIBBON_VERTICES; i++) {
        tint.set(strip ? [strip.white, strip.white, strip.white, strip.alpha] : [0, 0, 0, 0], i * 4);
        params.set(strip ? [strip.glow, strip.fill, 0, 0] : [0, 0, 0, 0], i * 4);
      }
    }
    this.tint.needsUpdate = true;
    this.params.needsUpdate = true;
    this.trail.reset(look.lifeMs);
    this.state = "ativa";
    this.mesh.visible = false;
  }

  sample(now: number, ax: number, ay: number, az: number, bx: number, by: number, bz: number): void {
    this.trail.sample(now, ax, ay, az, bx, by, bz);
  }

  /**
   * O golpe acertou forte (Fase 14, o crítico): o brilho e o preenchimento ficam `factor` vezes os do
   * `begin` até o fim desta fita. Vários acertos no mesmo golpe (o golpe em vários alvos) não somam.
   */
  boost(factor: number): void {
    const u = this.material.uniforms;
    u["uGlow"]!.value = this.baseGlow * factor;
    u["uFill"]!.value = this.baseFill * factor;
  }

  release(now: number): void {
    if (this.state !== "ativa") return;
    this.trail.release(now);
    this.state = "soltando";
    this.releasedAt = now;
  }

  /** Refaz a fita do quadro; devolve `false` quando acabou (fica livre e invisível). */
  update(now: number): boolean {
    if (this.state === "livre") return false;
    if (this.trail.done(now)) {
      this.stop();
      return false;
    }
    // Cada faixa no seu trecho do buffer; o resto do trecho, degenerado no último vértice (sem área).
    const out = this.position.array as Float32Array;
    const params = this.params.array as Float32Array;
    let n = 0;
    this.strips.forEach((strip, k) => {
      const region = out.subarray(k * RIBBON_VERTICES * 3, (k + 1) * RIBBON_VERTICES * 3);
      const v = this.trail.build(now, this.pivot, region, strip.from, strip.to, this.sheet ? this.meta : undefined);
      for (let i = v; i < RIBBON_VERTICES; i++) region.copyWithin(i * 3, Math.max(0, v - 1) * 3, Math.max(0, v - 1) * 3 + 3);
      // A folha: o lado em z e 1 + a idade em w (o programa das peças lê w > 0,5 como folha).
      if (this.sheet) {
        for (let i = 0; i < v; i++) {
          const o = (k * RIBBON_VERTICES + i) * 4;
          params[o + 2] = this.meta[i * 2]!;
          params[o + 3] = 1 + this.meta[i * 2 + 1]!;
        }
      }
      n = Math.max(n, v);
    });
    this.geometry.setDrawRange(0, n > 0 ? this.strips.length * RIBBON_VERTICES : 0);
    this.position.needsUpdate = n > 0;
    this.params.needsUpdate = n > 0 && this.sheet;
    this.material.uniforms["uAlpha"]!.value = this.alpha * this.trail.fade(now);
    this.mesh.visible = n > 0;
    return true;
  }

  /** Livre e invisível. */
  stop(): void {
    this.state = "livre";
    this.mesh.visible = false;
    this.geometry.setDrawRange(0, 0);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * Um pool de fitas de tamanho fixo (F92): todas criadas no começo, invisíveis até o uso. Sem fita livre,
 * a que está sumindo há mais tempo é reaproveitada; uma que ainda segue nunca sai, e quem pede além do
 * teto fica sem fita.
 */
export class RibbonPool {
  readonly ribbons: readonly Ribbon[];
  readonly capacity: number;

  constructor(root: Object3D, capacity: number) {
    this.capacity = capacity;
    this.ribbons = Array.from({ length: capacity }, () => {
      const r = new Ribbon();
      root.add(r.mesh);
      return r;
    });
  }

  /** Uma fita começando com `look`, ou `undefined` com todas seguindo algo. */
  acquire(look: RibbonLook): Ribbon | undefined {
    let pick: Ribbon | undefined;
    for (const r of this.ribbons) {
      if (r.state === "livre") {
        pick = r;
        break;
      }
      if (r.state === "soltando" && (!pick || r.releasedAt < pick.releasedAt)) pick = r;
    }
    pick?.begin(look);
    return pick;
  }

  update(now: number): void {
    for (const r of this.ribbons) r.update(now);
  }

  /** Todas livres (a cena que recomeça ou a vitrine). */
  clear(): void {
    for (const r of this.ribbons) r.stop();
  }

  get stats(): { active: number; fading: number; visible: number; capacity: number } {
    let active = 0;
    let fading = 0;
    let visible = 0;
    for (const r of this.ribbons) {
      if (r.state === "ativa") active++;
      if (r.state === "soltando") fading++;
      if (r.mesh.visible) visible++;
    }
    return { active, fading, visible, capacity: this.capacity };
  }

  dispose(): void {
    for (const r of this.ribbons) r.dispose();
  }
}

interface SwingState {
  ribbon: Ribbon | undefined;
  /** O progresso do golpe no quadro anterior (0 sem golpe): um golpe novo recomeça a fita. */
  swing: number;
  item: string;
  /** Neste golpe já se pediu fita e não veio (pool cheio, "Efeitos dos aliados" em 0): não pede de novo. */
  refused: boolean;
}

/**
 * As fitas dos golpes (F92), uma por corpo. A cada quadro, `frame` diz se o corpo segue com fita e devolve a
 * que deve receber a amostra. A fita sai (some pela cauda) quando o golpe acaba, quando começa outro e
 * quando o item troca; some na hora quando o corpo sai da tela ou da animação a cada quadro (a pose
 * ficaria parada ou aos saltos, e a fita fora da tela ainda custaria uma draw call). Sem vaga, ou sem
 * visual (`look` devolve `undefined`), o corpo fica sem fita até o próximo golpe.
 */
export class SwingRibbons {
  private readonly pool: RibbonPool;
  private readonly states = new Map<string, SwingState>();
  private readonly look: (id: string) => RibbonLook | undefined;

  constructor(root: Object3D, look: (id: string) => RibbonLook | undefined, capacity = SWING_RIBBONS) {
    this.pool = new RibbonPool(root, capacity);
    this.look = look;
  }

  /**
   * Um quadro do corpo `id`: o progresso do golpe (0 sem golpe; o arco e o corpo caído passam 0), o item na
   * mão e se o item está na tela, inteiro e animado a cada quadro. Devolve a fita a amostrar, ou nada.
   */
  frame(id: string, now: number, swing: number, item: string, visible: boolean): Ribbon | undefined {
    let s = this.states.get(id);
    if (!s) {
      s = { ribbon: undefined, swing: 0, item, refused: false };
      this.states.set(id, s);
    }
    const active = swing > 0 && swing < 1;
    const fresh = active && (s.swing === 0 || swing < s.swing);
    if (s.ribbon && !visible) {
      s.ribbon.stop();
      s.ribbon = undefined;
    }
    if (s.ribbon && (!active || fresh || item !== s.item)) {
      s.ribbon.release(now);
      s.ribbon = undefined;
    }
    if (!active || fresh) s.refused = false;
    s.swing = active ? swing : 0;
    s.item = item;
    if (!active || !visible || s.ribbon || s.refused) return s.ribbon;
    const look = this.look(id);
    s.ribbon = look && look.alpha > 0 ? this.pool.acquire(look) : undefined;
    s.refused = s.ribbon === undefined;
    return s.ribbon;
  }

  /** O golpe de `id` acertou forte (Fase 14): a fita que ele segue agora fica mais clara (nenhuma, nada). */
  boost(id: string, factor: number): void {
    this.states.get(id)?.ribbon?.boost(factor);
  }

  /** O corpo saiu da partida: a fita dele some pela cauda. */
  remove(id: string, now: number): void {
    this.states.get(id)?.ribbon?.release(now);
    this.states.delete(id);
  }

  update(now: number): void {
    this.pool.update(now);
  }

  get stats(): { active: number; fading: number; visible: number; capacity: number } {
    return this.pool.stats;
  }

  dispose(): void {
    this.pool.dispose();
    this.states.clear();
  }
}
